import {
    ConfigView,
    ConfigViewMetric,
    ConfigViewPillar,
    Layout,
    Metric,
} from './config.insight.model';
import { MetricDataItem } from './performance-data/metric.pd.model';

export enum VisualItemHoverAction {
    ENTER,
    LEAVE,
}

export interface VisualItemClickEvent {
    viewPillar: ConfigViewPillar;
    viewMetric: ConfigViewMetric;
    metric: Metric;
    dataMetric: MetricDataItem;
}

export interface VisualItemHoverEvent {
    action: VisualItemHoverAction;
    id: string;
    viewMetric: ConfigViewMetric;
    metric: Metric;
    dataMetric: MetricDataItem;
    peerGroupDataMetric: MetricDataItem;
}

export interface VisualItemLayout {
    layouts: Layout[];
    children: VisualItemLayout[];
    layout?: Layout;
    styleObj?: any;
}

function mapViewLayouts(
    view: ConfigView | ConfigViewPillar[],
): VisualItemLayout[] {
    if (!view) {
        return null;
    }
    const vv: Array<ConfigView | ConfigViewPillar> = Array.isArray(view)
        ? view
        : [view];
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    return (
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        vv &&
        vv.map((e) => ({
            // eslint-disable-next-line no-prototype-builtins
            name: e.hasOwnProperty('viewIdentifier')
                ? (e as any).viewIdentifier
                : (e as any).name,
            layouts: e.layouts,
            children: mapViewLayouts(e.pillars),
        }))
    );
}

function flattenLayouts(
    vils: VisualItemLayout[],
    layouts: Layout[] = [],
): Layout[] {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    vils &&
        vils.forEach((e) => {
            layouts.push(...e.layouts);
            flattenLayouts(e.children, layouts);
        }, []);
    return layouts;
}

function applyLayout(vil: VisualItemLayout, layout: Layout): void {
    vil.layout = layout;
    const nBorders = layout.itemsPerRow - 1;
    const distGrps = (100 - nBorders) / 12;
    const width = layout.width * distGrps;
    vil.styleObj['flex.%'] = `0 0 ${width}`;
    vil.styleObj['max-width.%'] = width;
}

export class LayoutQuery {
    private _mqls: Array<{ breakpoint: number; mql: MediaQueryList }>;
    private _callback: () => void;
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private readonly _onMqlChange = () => {
        this.style();
        if (this._callback) {
            this._callback();
        }
    };

    vils: VisualItemLayout[];
    otherHaveChildren: boolean;

    private _style(
        vils: VisualItemLayout[],
        mqls: Array<{ breakpoint: number; mql: MediaQueryList }>,
    ): void {
        if (!vils) {
            return;
        }
        vils.forEach((e) => {
            e.layout = null;
            e.styleObj = {};
            for (const ee of mqls) {
                const layout = e.layouts.find(
                    (eee) => eee.breakpoint === ee.breakpoint,
                );
                if (layout) {
                    applyLayout(e, layout);
                    break;
                }
            }
            if (!e.layout) {
                applyLayout(e, e.layouts[e.layouts.length - 1]);
            }
            this._style(e.children, mqls);
        });
    }

    style(): any {
        const mqls = this._mqls.filter((e) => e.mql.matches);
        this._style(this.vils, mqls);
        console.log(this.vils);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    bind(view: ConfigView, callback: () => void) {
        this.unbind();
        this.vils = mapViewLayouts(view);
        this.otherHaveChildren =
            this.vils[0].children.findIndex(
                (e) => e.children && e.children.length !== 0,
            ) !== -1;
        const layouts = flattenLayouts(this.vils).reduce((p, c) => {
            // eslint-disable-next-line @typescript-eslint/prefer-includes
            if (p.indexOf(c.breakpoint) === -1) {
                p.push(c.breakpoint);
            }
            return p;
        }, []);
        this._mqls = layouts
            .map((e) => ({
                breakpoint: e,
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                mql: window.matchMedia(`(min-width: ${e}px`),
            }))
            .sort((a, b) => b.breakpoint - a.breakpoint);
        this._callback = callback;
        this._mqls.forEach((e) => e.mql.addListener(this._onMqlChange));
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    unbind() {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._mqls &&
            this._mqls.forEach((e) => e.mql.removeListener(this._onMqlChange));
        this._callback = undefined;
    }
}
